import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Shield, FileText, Home, Users, CheckCircle } from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 md:py-24 bg-[#003366] text-white overflow-hidden mt-20">
        {/* Blueprint pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v100H0z' fill='none' stroke='%23ffffff' stroke-width='0.5'/%3E%3Cpath d='M20 0v100M40 0v100M60 0v100M80 0v100M0 20h100M0 40h100M0 60h100M0 80h100' stroke='%23ffffff' stroke-width='0.25'/%3E%3C/svg%3E")`,
          backgroundSize: '100px 100px'
        }}></div>
        {/* Logo Watermark */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img src="https://customer-assets.emergentagent.com/job_waitlist-system-5/artifacts/0d36dd51_Transparent%20Png-02.png" alt="" className="w-[400px] md:w-[500px] h-auto opacity-[0.06]" />
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="mb-6">
            <span className="block text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-2">
              About
            </span>
            <span className="block text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight">
              <span className="text-[#CFB973]">Valenrock<sup className="text-[0.5em]">™</sup></span>
            </span>
          </h1>
          
          {/* Decorative line */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-0.5 bg-[#CFB973]"></div>
            <div className="w-2 h-2 bg-[#CFB973] rotate-45"></div>
            <div className="w-12 h-0.5 bg-[#CFB973]"></div>
          </div>
          
          <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            A Different Kind of <span className="text-[#CFB973] font-semibold">Home Partnership</span>
          </p>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-[#CFB973] flex items-center justify-center">
              <Home className="w-6 h-6 text-[#003366]" />
            </div>
            <h2 className="text-3xl font-bold text-[#003366]">Our Story</h2>
          </div>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              At Valenrock Home Services, we believe the relationship between a homeowner and a contractor is often broken. For too long, the industry has been defined by "emergency fixes" and high-pressure upselling. Homeowners are often left wondering: <em>Do I really need this $20,000 repair, or am I just being sold?</em>
            </p>
            <p className="text-[#003366] font-semibold text-xl mb-8">
              We founded Valenrock to close that trust gap.
            </p>
          </div>
        </div>
      </section>

      {/* Consultant-First Approach */}
      <section className="py-16 bg-[#E8EEF3]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-[#CFB973] flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-[#003366]" />
            </div>
            <h2 className="text-3xl font-bold text-[#003366]">The Consultant-First Approach</h2>
          </div>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              We operate on a simple, revolutionary principle: <strong>Assessment before execution.</strong> By decoupling the diagnostic process from the construction work, we provide you with an objective, expert roadmap for your home's health.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Our signature Whole-Home Assessment isn't a sales pitch—it's a professional evaluation. We look at your home as a complete system, from the foundation to the roof, identifying what needs immediate attention and what can wait.
            </p>
          </div>
        </div>
      </section>

      {/* Data-Driven Home Care */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-[#CFB973] flex items-center justify-center">
              <FileText className="w-6 h-6 text-[#003366]" />
            </div>
            <h2 className="text-3xl font-bold text-[#003366]">Data-Driven Home Care</h2>
          </div>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              In 48 hours, we provide every client with a digital <strong>Home Profile™</strong>. This isn't just a list of chores; it's a strategic asset for your greatest investment.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Whether you are navigating the aging infrastructure of a historic New Jersey property or looking to modernize a mid-century gem, our reports give you the clarity to invest wisely.
            </p>
          </div>
        </div>
      </section>

      {/* Integrity Section */}
      <section className="py-16 bg-[#E8EEF3]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-[#CFB973] flex items-center justify-center">
              <Shield className="w-6 h-6 text-[#003366]" />
            </div>
            <h2 className="text-3xl font-bold text-[#003366]">Integrity in Every Square Foot</h2>
          </div>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              With over a decade of experience and a deep commitment to the communities of Northern and Central New Jersey, we bring a project manager's precision to every walk-through.
            </p>
            <p className="text-gray-700 leading-relaxed">
              When you're ready to move from planning to renovation, you aren't just hiring a crew—you're partnering with a team that already knows your home's history, its quirks, and its potential.
            </p>
          </div>
        </div>
      </section>

      {/* Closing Statement */}
      <section className="py-20 bg-[#003366]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-3xl md:text-4xl font-bold text-white mb-8">
            We don't just fix houses.<br />
            <span className="text-[#CFB973]">We protect homes.</span>
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/assessment">
              <Button className="bg-[#CFB973] hover:bg-[#b8a55e] text-[#003366] font-semibold px-8 py-3 text-lg">
                Book Your Assessment
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#003366] px-8 py-3 text-lg">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Credentials Section */}
      <section className="py-12 bg-[#E8EEF3]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12 mb-8">
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 rounded-full bg-[#CFB973] flex items-center justify-center mb-2 shadow-lg">
                <Shield className="w-8 h-8 text-[#003366]" />
              </div>
              <p className="text-[#003366] font-bold text-sm">Licensed & Insured</p>
              <p className="text-gray-500 text-xs">#13VH13590600</p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 rounded-full bg-[#CFB973] flex items-center justify-center mb-2 shadow-lg">
                <Users className="w-8 h-8 text-[#003366]" />
              </div>
              <p className="text-[#003366] font-bold text-sm">10+ Years</p>
              <p className="text-gray-500 text-xs">Experience</p>
            </div>
          </div>
          
          {/* EPA Certification */}
          <div className="flex flex-col items-center pt-6 border-t border-[#003366]/10">
            <img 
              src="https://customer-assets.emergentagent.com/job_smarthouse-staging/artifacts/inesgu3y_IMG_7280.png" 
              alt="EPA Lead-Safe Certified Firm" 
              className="h-24 md:h-28 w-auto object-contain mb-2 mix-blend-multiply"
            />
            <p className="text-[#003366] font-bold text-sm">EPA Lead-Safe Certified Firm</p>
            <p className="text-gray-500 text-xs">NAT-F326598-1</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
